import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, XCircle, User, Briefcase, ArrowRight } from 'lucide-react';

interface ProspectingValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  contactId: string;
  missingFields: {
    contact: string[];
    deal: string[];
  };
}

export default function ProspectingValidationModal({
  isOpen,
  onClose,
  dealId,
  contactId,
  missingFields,
}: ProspectingValidationModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const totalMissing = missingFields.contact.length + missingFields.deal.length;

  const handleNavigateToContact = () => {
    onClose();
    navigate(`/contact-detail?id=${contactId}`);
  };

  const handleNavigateToDeal = () => {
    onClose();
    navigate(`/deal-detail?id=${dealId}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Campos Obrigatórios para Prospecting
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="destructive">{totalMissing} campo(s) pendente(s)</Badge>
            <span className="text-sm text-muted-foreground">
              Preencha os campos abaixo para avançar
            </span>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {/* Contact Fields */}
            {missingFields.contact.length > 0 && (
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Contato</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleNavigateToContact}
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    Ir para Contato
                  </Button>
                </div>

                <div className="space-y-1">
                  {missingFields.contact.map((field, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm"
                    >
                      <XCircle className="h-4 w-4 text-destructive" />
                      <span>{field}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deal Fields */}
            {missingFields.deal.length > 0 && (
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Deal</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleNavigateToDeal}
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    Ir para Deal
                  </Button>
                </div>

                <div className="space-y-1">
                  {missingFields.deal.map((field, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm"
                    >
                      <XCircle className="h-4 w-4 text-destructive" />
                      <span>{field}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
